<template>
    <div class="vue-occasions-overview">
        <div class="row">
            <div class="region-filters col-lg-12 col-xl-3">
                <template v-if="hasFilters === true">
                    <nexdrive-filter-toggle
                        aria-controls-id="filters"
                        aria-label-name="Occasion filter"
                    >
                    </nexdrive-filter-toggle>

                    <div id="filters">
                        <div class="col-12">
                            <h2 class="filter-title d-flex justify-content-between align-items-end my-3">
                                {{ filterTitle }}
                                <button class="reset-filters virtual-p" @click="handleResetFilters">{{ resetFiltersTxt }}
                                </button>
                            </h2>
                        </div>

                        <div class="garage-filter vue-filter-wrapper filter col-12" v-if="hasGarage === true">
                            <h3 class="virtual-p font-weight-bold">{{ garageTxt }}</h3>
                            <p class="mb-0">{{ selectedGarage.value }}</p>
                        </div>

                        <occasion-filter-group :title="makeAndModelTxt" :show-open="true">
                            <occasion-dropdown-filter :event-bus="eventBus" :placeholder="makeTxt"
                                                      :options="filters.field_brand"
                                                      filter-key="field_brand"></occasion-dropdown-filter>
                            <occasion-dropdown-filter v-if="showModelDropdown" :event-bus="eventBus" label="name"
                                                      :placeholder="modelTxt" :options="filters.field_model"
                                                      filter-key="field_model"></occasion-dropdown-filter>
                        </occasion-filter-group>

                        <occasion-filter-group
                            v-if="filters.field_construction_year.min !== filters.field_construction_year.max"
                            :title="constructionYearTxt">
                            <occasion-range-filter
                                :event-bus="eventBus"
                                :step.number="1"
                                :show-meta="true"
                                :min="filters.field_construction_year.min"
                                :max="filters.field_construction_year.max"
                                filter-key="field_construction_year"
                            ></occasion-range-filter>
                        </occasion-filter-group>

                        <occasion-filter-group v-if="filters.field_mileage.min !== filters.field_mileage.max"
                                               :title="mileageTxt">
                            <occasion-range-filter
                                :event-bus="eventBus"
                                :formatter="value => `${value} km`"
                                :step.number="1000"
                                :show-meta="true"
                                :min="filters.field_mileage.min"
                                :max="filters.field_mileage.max"
                                filter-key="field_mileage"
                            ></occasion-range-filter>
                        </occasion-filter-group>

                        <occasion-filter-group v-if="filters.field_price.min !== filters.field_price.max" :title="priceTxt">
                            <occasion-range-filter
                                :event-bus="eventBus"
                                :formatter="value => `$ ${value}`"
                                :step.number="500"
                                :show-meta="true"
                                :min="filters.field_price.min"
                                :max="filters.field_price.max"
                                filter-key="field_price"
                            ></occasion-range-filter>
                        </occasion-filter-group>

                        <occasion-filter-group v-if="hasOtherOptions" :title="otherOptionsTxt">
                            <occasion-dropdown-filter :event-bus="eventBus" :placeholder="transmissionTxt"
                                                      :options="filters.field_transmission"
                                                      filter-key="field_transmission"></occasion-dropdown-filter>
                            <occasion-dropdown-filter :event-bus="eventBus" :placeholder="carBodyTxt"
                                                      :options="filters.field_bodywork"
                                                      filter-key="field_bodywork"></occasion-dropdown-filter>
                        </occasion-filter-group>

                        <occasion-filter-group :title="colorTxt" v-if="filters.field_main_color.length > 1">
                            <occasion-dropdown-filter :event-bus="eventBus" :placeholder="colorTxt"
                                                      :options="filters.field_main_color"
                                                      filter-key="field_main_color"></occasion-dropdown-filter>
                        </occasion-filter-group>

                        <occasion-filter-group :title="doorsTxt" v-if="filters.field_number_of_doors.length > 1">
                            <occasion-dropdown-filter :event-bus="eventBus" :placeholder="doorsTxt"
                                                      :options="filters.field_number_of_doors"
                                                      filter-key="field_number_of_doors"></occasion-dropdown-filter>
                        </occasion-filter-group>

                        <occasion-filter-group :title="cilindersTxt"
                                               v-if="filters.field_amount_of_cylinders.length > 1">
                            <occasion-dropdown-filter :event-bus="eventBus" :placeholder="cilindersTxt"
                                                      :options="filters.field_amount_of_cylinders"
                                                      filter-key="field_amount_of_cylinders"></occasion-dropdown-filter>
                        </occasion-filter-group>

                        <occasion-filter-group :title="garageTxt" v-if="filters.field_garage.length > 1">
                            <occasion-dropdown-filter :event-bus="eventBus" :placeholder="garageTxt"
                                                      :options="filters.field_garage"
                                                      filter-key="field_garage"></occasion-dropdown-filter>
                        </occasion-filter-group>
                    </div>

                    <nexdrive-filter-toggle
                        aria-controls-id="filters"
                        :aria-label-name="occasionFilterTxt"
                        :open-text="showResultsTxt"
                        additional-classes="show-results"
                        :close-text="showItemsTxt">
                    </nexdrive-filter-toggle>
                </template>
            </div>

            <div class="col-lg-12 col-xl-9 mt-3 mt-xl-0">
                <div class="occasion-metas row mb-3 mb-md-0">
                    <div class="occasion-results col-md-5">
                        <p class="my-3">
                            <template v-if="meta.items === 1">{{ oneResultTxt }}</template>
                            <template v-else>{{ meta.items }} {{ resultsTxt }}</template>
                        </p>
                    </div>

                    <div class="occasion-sort col-md-7 text-right d-flex align-items-center justify-content-end">
                        <label for="sort-select" class="mb-0">{{ sortTxt }}</label>

                        <select class="form-control ml-3" id="sort-select" v-model="sort">
                            <option value="created_at-DESC">{{ dateDescSortTxt }}</option>
                            <option value="created_at-ASC">{{ dateAscSortTxt }}</option>
                            <option value="field_price-DESC">{{ priceDescSortTxt }}</option>
                            <option value="field_price-ASC">{{ priceAscSortTxt }}</option>
                            <option value="field_construction_year-ASC">{{ constructionYearDescSortTxt }}</option>
                            <option value="field_construction_year-DESC">{{ constructionYearAscSortTxt }}</option>
                            <option value="field_mileage-ASC">{{ mileageDescSortTxt }}</option>
                            <option value="field_mileage-DESC">{{ mileageAscSortTxt }}</option>
                        </select>
                    </div>
                </div>
                <template v-if="hasItems === true">
                    <div class="occasion-list-wrapper" :class="{'loading-occasions': isLoading}">
                        <occasion-list-item v-for="(item, index) in items" :key="`occasion-${index}`"
                                            :item="item"></occasion-list-item>

                        <loader v-if="infiniteScrollActive === true"
                                @loader-visibility="handleLoaderVisibility"></loader>
                    </div>
                </template>

                <template v-if="hasItems === false">
                    <h3>Geen resultaten</h3>
                    <p>Geen resultaten, pas de filters aan.</p>
                </template>
            </div>
        </div>
    </div>
</template>
<script>
import occasionOverviewMixin from "../mixins/occasionOverviewMixin";

export default {
    name: "OccasionOverview",

    mixins: [occasionOverviewMixin],

    data() {
        return {
            isLoading: false,
            infiniteScrollActive: false,
            garageTxt: window.Drupal.t('Garage'),
            resetFiltersTxt: window.Drupal.t('Reset filters'),
            makeTxt: window.Drupal.t('Make'),
            makeAndModelTxt: window.Drupal.t('Make and model'),
            filterTitle: window.Drupal.t('Filter'),
            modelTxt: window.Drupal.t('Model'),
            mileageTxt: window.Drupal.t('Mileage'),
            constructionYearTxt: window.Drupal.t('Construction year'),
            priceTxt: window.Drupal.t('Price'),
            otherOptionsTxt: window.Drupal.t('Other options'),
            transmissionTxt: window.Drupal.t('Transmission'),
            carBodyTxt: window.Drupal.t('Body'),
            colorTxt: window.Drupal.t('Color'),
            doorsTxt: window.Drupal.t('Number of doors'),
            cilindersTxt: window.Drupal.t('Number of cilinders'),
            occasionFilterTxt: window.Drupal.t('Occasion filter'),
            showResultsTxt: window.Drupal.t('Show results'),
            oneResultTxt: window.Drupal.t('1 result'),
            resultsTxt: window.Drupal.t('results'),
            sortTxt: window.Drupal.t('Sort'),
            dateDescSortTxt: window.Drupal.t('Last posted descending'),
            dateAscSortTxt: window.Drupal.t('Last posted ascending'),
            priceDescSortTxt: window.Drupal.t('Price descending'),
            priceAscSortTxt: window.Drupal.t('Price ascending'),
            constructionYearDescSortTxt: window.Drupal.t('Construction year descending'),
            constructionYearAscSortTxt: window.Drupal.t('Construction year ascending'),
            mileageDescSortTxt: window.Drupal.t('Mileage descending'),
            mileageAscSortTxt: window.Drupal.t('Mileage ascending'),
        }
    },

    computed: {
        hasItems() {
            return !_.isEmpty(this.items);
        },

        hasOtherOptions() {
            return this.filters.field_transmission.length > 1
                && this.filters.field_bodywork.length > 1
        },

        hasLocalStorage() {
            return !_.isEmpty(localStorage.getItem('appliedFilters'));
        },

        showItemsTxt() {
            return window.Drupal.t('Show @amount results', {
                '@amount': this.meta.items
            });
        }
    },

    mounted() {
        const urlParams = new URLSearchParams(window.location.search);
        if (!_.isNil(urlParams.get('vestiging'))) {
            const filterValue = [{
                "key": "field_garage",
                "value": urlParams.get('vestiging'),
                "type": "dropdown"
            }];
            localStorage.setItem('appliedFilters', JSON.stringify(filterValue));
        }

        if (this.hasLocalStorage === true) {
            this.appliedFilters = JSON.parse(localStorage.getItem('appliedFilters'));
        }

        if (!_.isNil(localStorage.getItem('occasionOverviewSort'))) {
            this.sort = localStorage.getItem('occasionOverviewSort');
        }

        this.fetchData();
    },

    methods: {
        fetchData() {
            this.isLoading = true;

            axios.get(`/${window.drupalSettings.ef2_custom.lang_code}/api/occasions`, {
                params: {
                    filters: JSON.stringify(this.appliedFilters),
                    page: this.page,
                    sort: this.sort
                }
            }).then(response => {
                if (response.data.meta.currentPage === 1) {
                    this.items = response.data.data;
                    this.filters = response.data.filters;
                }

                if (response.data.meta.currentPage >= 2) {
                    this.items = this.$set(this, 'items', [...this.items, ...response.data.data]);
                }

                this.meta = response.data.meta;
                this.infiniteScrollActive = !_.isNil(response.data.meta.nextPage);
            }).catch(error => {
                //alert('Oops! Er gaat iets mis..');
            }).finally(() => {
                this.isLoading = false;

                if (this.hasLocalStorage === true) {
                    this.handleLocalStorageFilters();
                }
            });
        },

        handleLoaderVisibility(payload) {
            if (!payload.visible || this.isLoading === true) {
                return;
            }

            this.page++;
            this.fetchData();
        },

        handleLocalStorageFilters() {
            _.forEach(this.appliedFilters, (appliedFilter) => {
                if (appliedFilter.type === 'range') {
                    let filter = this.filters[appliedFilter.key];

                    this.$set(filter, 'populatedValue', [appliedFilter.min, appliedFilter.max]);
                }

                if (appliedFilter.type === 'dropdown') {
                    let filter = this.filters[appliedFilter.key];

                    this.$set(filter, 'populatedValue', ...[appliedFilter.value]);
                }
            });
        },

        handleResetFilters() {
            this.eventBus.$emit('reset-filters');
            this.appliedFilters = [];

            if (!_.isNil(window.defaultGarage)) {
                localStorage.setItem("appliedFilters", JSON.stringify(window.defaultGarage));
                this.appliedFilters = JSON.parse(localStorage.getItem('appliedFilters'));
            }

            this.page = 1;
            this.fetchData();
        }
    },

    watch: {
        sort(newValue) {
            localStorage.setItem('occasionOverviewSort', newValue);
            this.page = 1;
            this.fetchData();
        }
    }
}
</script>
